/* ==========================================================================
   Hiva Jewels — PDP behaviour
   Each widget is independent and no-ops if its markup is absent, so blocks
   can be reordered or switched off in the theme editor without breaking.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------
     Offer codes — copy to clipboard
     --------------------------------------------------------------------- */

  function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }
    // Safari/http fallback.
    return new Promise(function (resolve, reject) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy') ? resolve() : reject();
      } catch (e) {
        reject(e);
      } finally {
        document.body.removeChild(ta);
      }
    });
  }

  function initOffers(root) {
    root.querySelectorAll('[data-hiva-copy]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var code = btn.getAttribute('data-hiva-copy');
        var original = btn.getAttribute('data-label-idle') || btn.textContent.trim();
        copyText(code)
          .then(function () {
            btn.textContent = btn.getAttribute('data-label-done') || 'Copied';
            btn.setAttribute('data-copied', 'true');
            // aria-label overrides the text for screen readers, so update it
            // too or the state change is never announced.
            btn.setAttribute('aria-label', 'Discount code ' + code + ' copied');
          })
          .catch(function () {
            // Copy blocked: surface the code so it can still be selected.
            btn.textContent = code;
            btn.setAttribute('aria-label', 'Copy failed. Discount code is ' + code);
          })
          .then(function () {
            setTimeout(function () {
              btn.textContent = original;
              btn.removeAttribute('data-copied');
              btn.setAttribute('aria-label', 'Copy discount code ' + code);
            }, 1800);
          });
      });
    });
  }

  /* ---------------------------------------------------------------------
     Delivery estimator

     Derived strictly from the published policy:
       dispatch  = within 48 business hours
       transit   = 4-7 working days
     Both legs skip weekends. Presented as an estimate — there is no courier
     serviceability lookup wired in, so we never claim a guaranteed date or
     pincode-specific promise.
     --------------------------------------------------------------------- */

  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Calendar days, not business days. Stacking business-day arithmetic for
  // dispatch AND transit compounded to 8-13 calendar days, which badly
  // overstated real performance (~5-6 days, next-day locally). Zone figures
  // are door-to-door totals and already include dispatch.
  // Sunday is skipped only as a final landing day — couriers here deliver
  // Mon-Sat, so a date that falls on Sunday realistically arrives Monday.
  function addDays(from, days) {
    var d = new Date(from.getTime());
    d.setDate(d.getDate() + days);
    if (d.getDay() === 0) d.setDate(d.getDate() + 1);
    return d;
  }

  function fmt(d) {
    return d.getDate() + ' ' + MONTHS[d.getMonth()];
  }

  // Longest matching PIN prefix wins, so "302" (Jaipur) beats "30" (Rajasthan).
  function resolveZone(pin, zones, dmin, dmax) {
    var best = null;
    var bestLen = -1;
    for (var i = 0; i < zones.length; i++) {
      var z = zones[i];
      var list = z.p || [];
      for (var j = 0; j < list.length; j++) {
        var pre = String(list[j]);
        if (pre && pin.indexOf(pre) === 0 && pre.length > bestLen) {
          best = z;
          bestLen = pre.length;
        }
      }
    }
    return best || { min: dmin, max: dmax, label: '' };
  }

  // Indian PIN codes are 6 digits and never start with 0.
  function validPin(v) {
    return /^[1-9][0-9]{5}$/.test(v);
  }

  function initDelivery(root) {
    var form = root.querySelector('[data-hiva-pin-form]');
    if (!form) return;

    var input = form.querySelector('[data-hiva-pin-input]');
    var result = root.querySelector('[data-hiva-pin-result]');
    if (!input || !result) return;

    var defaultMin = parseInt(form.getAttribute('data-default-min'), 10) || 4;
    var defaultMax = parseInt(form.getAttribute('data-default-max'), 10) || 6;
    var cod = form.getAttribute('data-cod') === 'true';

    var zones = [];
    try {
      zones = JSON.parse(form.getAttribute('data-zones') || '[]') || [];
    } catch (e) {
      zones = []; // malformed zone table: fall back to the default range
    }

    function show(state, html) {
      result.setAttribute('data-state', state);
      result.innerHTML = html;
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var pin = (input.value || '').trim();

      if (!validPin(pin)) {
        input.setAttribute('aria-invalid', 'true');
        show('error', 'Please enter a valid 6-digit PIN code.');
        return;
      }

      input.removeAttribute('aria-invalid');

      var zone = resolveZone(pin, zones, defaultMin, defaultMax);
      var now = new Date();
      var from = addDays(now, zone.min);
      var to = addDays(now, zone.max);

      // Same min and max (or collapsed by the Sunday shift) reads better as a
      // single date than as "23 Jul – 23 Jul".
      var when = fmt(from) === fmt(to)
        ? 'Arriving ' + fmt(from)
        : 'Arriving ' + fmt(from) + ' &ndash; ' + fmt(to);

      var where = zone.label
        ? 'Estimated for ' + zone.label + ' (' + pin + ').'
        : 'Estimated for ' + pin + '.';

      var html =
        '<span class="hiva-pin__eta">' + when + '</span>' +
        '<br>' + where +
        (cod ? ' Cash on Delivery available.' : '');

      show('ok', html);

      try {
        localStorage.setItem('hiva_pin', pin);
      } catch (err) { /* private mode */ }
    });

    // Restore a previously entered PIN for repeat visitors.
    try {
      var saved = localStorage.getItem('hiva_pin');
      if (saved && validPin(saved)) {
        input.value = saved;
        form.dispatchEvent(new Event('submit', { cancelable: true }));
      }
    } catch (err) { /* private mode */ }
  }

  /* ---------------------------------------------------------------------
     Sticky add-to-cart
     Delegates to the theme's real ATC button so the cart drawer, variant
     state and any app hooks keep working. We never re-implement cart POST.
     --------------------------------------------------------------------- */

  function initSticky() {
    var bar = document.querySelector('[data-hiva-sticky]');
    if (!bar) return;

    // boot() re-runs on every shopify:section:load (theme editor). Without this
    // guard each pass adds another scroll listener, another MutationObserver
    // and another click handler — so one tap would add several items to cart.
    if (bar.dataset.hivaBound === 'true') return;
    bar.dataset.hivaBound = 'true';

    var formId = bar.getAttribute('data-form-id');
    var realForm = formId ? document.getElementById(formId) : null;
    var realBtn = realForm ? realForm.querySelector('[name="add"]') : document.querySelector('product-form [name="add"]');
    var stickyBtn = bar.querySelector('[data-hiva-sticky-btn]');

    if (!realBtn || !stickyBtn) return;

    stickyBtn.addEventListener('click', function () {
      realBtn.click();
    });

    /* Buy now → Fastrr (Shiprocket) --------------------------------------
       Fastrr injects `button.sr-headless-checkout` into the product info
       column after its script boots, and keeps a second hidden copy as a
       template. Resolve the VISIBLE one and delegate to it so the sticky bar
       opens the identical Fastrr checkout. If it never appears, the button
       stays hidden rather than dead-ending the customer. */
    var buyBtn = bar.querySelector('[data-hiva-sticky-buy]');
    if (buyBtn) {
      var findFastrr = function () {
        var candidates = document.querySelectorAll('.sr-headless-checkout');
        for (var i = 0; i < candidates.length; i++) {
          var c = candidates[i];
          if (c.offsetParent !== null && c.offsetHeight > 0) return c;
        }
        return null;
      };

      // Resolve at click time, not once up front: Fastrr re-renders its button
      // on variant change, so a cached node goes stale and the click silently
      // dead-ends. Bind the handler once, look the button up each time.
      buyBtn.addEventListener('click', function () {
        var fastrr = findFastrr();
        if (fastrr) fastrr.click();
      });

      var wireBuy = function () {
        if (!findFastrr()) return false;
        buyBtn.hidden = false;
        return true;
      };

      if (!wireBuy()) {
        // Fastrr boots asynchronously and can be slow on poor connections, so
        // keep watching for 60s rather than giving up at 10s and losing the
        // button for the whole session.
        var tries = 0;
        var poll = setInterval(function () {
          if (wireBuy() || ++tries > 120) clearInterval(poll);
        }, 500);
      }
    }

    // Mirror sold-out / disabled state from the real button.
    function syncState() {
      var disabled = realBtn.hasAttribute('disabled');
      stickyBtn.disabled = disabled;
      var txt = realBtn.querySelector('span');
      if (txt && txt.textContent.trim()) {
        stickyBtn.textContent = txt.textContent.trim();
      }
    }
    syncState();

    new MutationObserver(syncState).observe(realBtn, {
      attributes: true,
      attributeFilter: ['disabled'],
      childList: true,
      subtree: true
    });

    // Keep the sticky price in step with variant changes.
    var priceTarget = bar.querySelector('[data-hiva-sticky-price]');
    var priceSource = document.querySelector('[id^="price-"] .price-item--regular, [id^="price-"] .price-item--sale');
    if (priceTarget && priceSource) {
      new MutationObserver(function () {
        var live = document.querySelector('[id^="price-"] .price-item--sale, [id^="price-"] .price-item--regular');
        if (live && live.textContent.trim()) priceTarget.textContent = live.textContent.trim();
      }).observe(priceSource.closest('[id^="price-"]') || priceSource, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    // Reveal the bar only once the real ATC has scrolled out of view.
    var visible = null;
    function show(on) {
      if (on === visible) return; // avoid touching the DOM on every frame
      visible = on;
      bar.setAttribute('data-visible', on ? 'true' : 'false');
      document.body.classList.toggle('hiva-sticky-on', on);
    }

    // A rAF-throttled scroll read rather than IntersectionObserver: IO depends
    // on the compositor and silently never fires in some non-painting contexts
    // (headless capture, background tabs), which would leave the bar stuck
    // hidden. One rect read per frame is cheap and behaves the same everywhere.
    var ticking = false;
    function update() {
      ticking = false;
      show(realBtn.getBoundingClientRect().bottom < 0);
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ---------------------------------------------------------------------
     FAQ accordion
     --------------------------------------------------------------------- */

  function initFaq(root) {
    root.querySelectorAll('[data-hiva-faq-q]').forEach(function (q) {
      q.addEventListener('click', function () {
        var panel = document.getElementById(q.getAttribute('aria-controls'));
        var open = q.getAttribute('aria-expanded') === 'true';
        q.setAttribute('aria-expanded', open ? 'false' : 'true');
        if (panel) panel.setAttribute('data-open', open ? 'false' : 'true');
      });
    });
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */

  function boot(scope) {
    var root = scope || document;
    root.querySelectorAll('[data-hiva-offers]').forEach(initOffers);
    root.querySelectorAll('[data-hiva-delivery]').forEach(initDelivery);
    root.querySelectorAll('[data-hiva-faq]').forEach(initFaq);
    initSticky();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); }, { once: true });
  } else {
    boot();
  }

  // Re-bind when the theme editor swaps a section in.
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
